"use client"

import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Container, Card, Table, Form, Button } from "react-bootstrap"
import { useAppDispatch, useAppSelector } from "../../../redux/hooks"
import {
  fetchAllGuests,
  fetchAdminMonthlySummary,
  deleteGuest,
} from "../../../redux/slices/guestsSlice"
import { fetchBuildings, updateGuestSettings } from "../../../redux/slices/buildingsSlice"
import { useTranslation } from "../../../hooks/use-translation"
import { useToast } from "../../../components/ui/use-toast"
import { useAuth } from "../../../contexts/auth-context"
import { Trash2, AlertTriangle, MessageSquare } from "lucide-react"
import "../../../styles/admin-buttons.css"

export default function AdminGuestsPage() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { adminGuests = [], adminSummary, isLoading } = useAppSelector((state) => state.guests)
  const { buildings = [] } = useAppSelector((state) => state.buildings)
  const { t } = useTranslation()
  const { toast } = useToast()
  const { user } = useAuth()

  const [buildingFilter, setBuildingFilter] = useState("all")
  const [monthFilter, setMonthFilter] = useState(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`
  })
  const [apartmentFilter, setApartmentFilter] = useState("all")
  const [showOverlapsOnly, setShowOverlapsOnly] = useState(false)

  // Guest settings form
  const [settingsForm, setSettingsForm] = useState({
    monthlyDayLimit: 0,
    showLimitWarning: true,
    showGuestHistoryToResidents: true,
  })

  // Filter buildings for restricted admins
  const managedBuildings = user?.managedBuildings || []
  const filteredBuildings =
    managedBuildings.length > 0
      ? buildings.filter((b) => managedBuildings.includes(b.id))
      : buildings

  useEffect(() => {
    dispatch(fetchBuildings(false))
  }, [dispatch])

  useEffect(() => {
    dispatch(fetchAllGuests({ building: buildingFilter, month: monthFilter }))
    if (buildingFilter && buildingFilter !== "all") {
      dispatch(fetchAdminMonthlySummary({ building: buildingFilter, month: monthFilter }))
    }
  }, [dispatch, buildingFilter, monthFilter])

  // Load settings when building changes
  useEffect(() => {
    if (buildingFilter && buildingFilter !== "all") {
      const building = buildings.find((b) => b.id === buildingFilter)
      if (building) {
        setSettingsForm({
          monthlyDayLimit: building.guestSettings?.monthlyDayLimit ?? 0,
          showLimitWarning: building.guestSettings?.showLimitWarning ?? true,
          showGuestHistoryToResidents: building.guestSettings?.showGuestHistoryToResidents ?? true,
        })
      }
    }
  }, [buildingFilter, buildings])

  const handleDeleteGuest = async (guestId: string) => {
    if (window.confirm(t("confirmDelete"))) {
      try {
        await dispatch(deleteGuest(guestId)).unwrap()
        toast({ title: t("success"), description: t("guestDeletedSuccessfully") })
        if (buildingFilter && buildingFilter !== "all") {
          dispatch(fetchAdminMonthlySummary({ building: buildingFilter, month: monthFilter }))
        }
      } catch (error: any) {
        toast({ title: t("error"), description: error || "Failed to delete", variant: "destructive" })
      }
    }
  }

  const handleSaveSettings = async () => {
    if (!buildingFilter || buildingFilter === "all") {
      toast({ title: t("error"), description: t("selectBuildingFirst"), variant: "destructive" })
      return
    }
    try {
      await dispatch(updateGuestSettings({ buildingId: buildingFilter, guestSettings: settingsForm })).unwrap()
      toast({ title: t("success"), description: t("settingsSaved") })
    } catch (error: any) {
      toast({ title: t("error"), description: error || "Failed to save settings", variant: "destructive" })
    }
  }

  // Compute overlapping guest IDs
  const overlappingGuestIds = new Set<string>()
  for (let i = 0; i < adminGuests.length; i++) {
    for (let j = i + 1; j < adminGuests.length; j++) {
      const a = adminGuests[i]
      const b = adminGuests[j]
      if (
        a.building === b.building &&
        a.roomNumber === b.roomNumber &&
        a.checkInDate < b.checkOutDate &&
        b.checkInDate < a.checkOutDate
      ) {
        overlappingGuestIds.add(a.id)
        overlappingGuestIds.add(b.id)
      }
    }
  }

  // Unique apartment numbers for filter
  const uniqueApartments = Array.from(new Set(adminGuests.map((g) => g.roomNumber)))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))

  const filteredGuests = adminGuests
    .filter((guest) => {
      const matchesApartment = apartmentFilter === "all" || guest.roomNumber === apartmentFilter
      const matchesOverlap = !showOverlapsOnly || overlappingGuestIds.has(guest.id)
      return matchesApartment && matchesOverlap
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

  const getBuildingName = (buildingId: string) => {
    const building = buildings.find((b) => b.id === buildingId)
    return building ? building.name : buildingId
  }

  return (
    <Container fluid className="py-4 px-2 px-md-4">
      <h1 className="fs-2 fw-bold mb-4 text-white">{t("guestManagement")}</h1>

      {/* Filters */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body className="p-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
            <div className="d-flex gap-2 flex-wrap">
              <Form.Group>
                <Form.Select value={buildingFilter} onChange={(e) => setBuildingFilter(e.target.value)}>
                  <option value="all">{t("allBuildings")}</option>
                  {filteredBuildings.map((building) => (
                    <option key={building.id} value={building.id}>
                      {building.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Control
                  type="month"
                  value={monthFilter}
                  onChange={(e) => setMonthFilter(e.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Select value={apartmentFilter} onChange={(e) => setApartmentFilter(e.target.value)}>
                  <option value="all">{t("allApartments")}</option>
                  {uniqueApartments.map((apt) => (
                    <option key={apt} value={apt}>
                      {apt}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Check
                type="switch"
                id="overlapToggle"
                label={t("showOverlapsOnly")}
                checked={showOverlapsOnly}
                onChange={(e) => setShowOverlapsOnly(e.target.checked)}
                className="align-self-center ms-2"
              />
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Monthly Summary */}
      {adminSummary && buildingFilter !== "all" && (
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body className="p-4">
            <h5 className="fw-semibold mb-3">
              {t("monthlySummary")} — {getBuildingName(buildingFilter)}
            </h5>
            <div className="d-flex gap-4 mb-3">
              <div>
                <span className="text-muted">{t("totalGuests")}:</span>{" "}
                <strong>{adminSummary.totalGuests}</strong>
              </div>
              <div>
                <span className="text-muted">{t("totalDays")}:</span>{" "}
                <strong>{adminSummary.totalDays}</strong>
              </div>
              {adminSummary.monthlyLimit > 0 && (
                <div>
                  <span className="text-muted">{t("monthlyLimit")}:</span>{" "}
                  <strong>{adminSummary.monthlyLimit} {t("daysPerMonth")}</strong>
                </div>
              )}
            </div>
            {adminSummary.apartments.length > 0 && (
              <div className="table-responsive">
                <Table hover className="align-middle" size="sm">
                  <thead className="table-light">
                    <tr>
                      <th className="fw-semibold text-gray-700">{t("apartment")}</th>
                      <th className="fw-semibold text-gray-700">{t("resident")}</th>
                      <th className="fw-semibold text-gray-700">{t("numberOfGuests")}</th>
                      <th className="fw-semibold text-gray-700">{t("totalDays")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminSummary.apartments.map((apt) => (
                      <tr
                        key={`${apt.userId}-${apt.roomNumber}`}
                        className={
                          adminSummary.monthlyLimit > 0 && apt.totalDays > adminSummary.monthlyLimit
                            ? "table-warning"
                            : ""
                        }
                      >
                        <td>{apt.roomNumber}</td>
                        <td>{apt.userFullName}</td>
                        <td>{apt.totalGuests}</td>
                        <td>
                          {apt.totalDays}
                          {adminSummary.monthlyLimit > 0 && apt.totalDays > adminSummary.monthlyLimit && (
                            <span className="text-danger ms-2">({t("exceedsLimit")})</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Guest Registrations */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body className="p-4">
          <h5 className="fw-semibold mb-3">{t("guestRegistrations")}</h5>
          {isLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle">
                <thead className="table-light">
                  <tr>
                    <th className="fw-semibold text-gray-700">{t("createdAt")}</th>
                    <th className="fw-semibold text-gray-700">{t("building")}</th>
                    <th className="fw-semibold text-gray-700">{t("apartment")}</th>
                    <th className="fw-semibold text-gray-700">{t("resident")}</th>
                    <th className="fw-semibold text-gray-700">{t("guestFullName")}</th>
                    <th className="fw-semibold text-gray-700">{t("guestIdNumber")}</th>
                    <th className="fw-semibold text-gray-700">{t("checkInDate")}</th>
                    <th className="fw-semibold text-gray-700">{t("checkOutDate")}</th>
                    <th className="fw-semibold text-gray-700">{t("totalDays")}</th>
                    <th className="fw-semibold text-gray-700">{t("notes")}</th>
                    {user?.isSuperAdmin && <th className="fw-semibold text-gray-700">{t("actions")}</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredGuests.length === 0 ? (
                    <tr>
                      <td colSpan={user?.isSuperAdmin ? 11 : 10} className="text-center py-4 text-muted">
                        {t("noGuestsFound")}
                      </td>
                    </tr>
                  ) : (
                    filteredGuests.map((guest) => (
                      <tr
                        key={guest.id}
                        className={`border-bottom ${overlappingGuestIds.has(guest.id) ? "table-danger" : ""}`}
                      >
                        <td className="align-middle">
                          {guest.createdAt ? (
                            <small>
                              {new Date(guest.createdAt).toLocaleDateString("el-GR")}
                              {" "}
                              {new Date(guest.createdAt).toLocaleTimeString("el-GR", {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </small>
                          ) : "-"}
                        </td>
                        <td className="align-middle">{getBuildingName(guest.building)}</td>
                        <td className="align-middle">{guest.roomNumber}</td>
                        <td className="align-middle">{guest.userFullName}</td>
                        <td className="align-middle">{guest.fullName}</td>
                        <td className="align-middle">{guest.idNumber}</td>
                        <td className="align-middle">
                          {new Date(guest.checkInDate).toLocaleDateString("el-GR")}
                          {overlappingGuestIds.has(guest.id) && (
                            <AlertTriangle size={14} className="text-danger ms-1" title={t("overlappingBooking")} />
                          )}
                        </td>
                        <td className="align-middle">
                          {new Date(guest.checkOutDate).toLocaleDateString("el-GR")}
                        </td>
                        <td className="align-middle">{guest.totalDays}</td>
                        <td className="align-middle">
                          <div>{guest.notes || "-"}</div>
                          <Button
                            size="sm"
                            variant="link"
                            className="p-0 mt-1 text-muted"
                            onClick={() => navigate(`/dashboard/admin/guests/${guest.id}/thread`)}
                            title={t("openThread")}
                          >
                            <MessageSquare size={14} className="me-1" />
                            <span className="small">
                              {t("thread")} {guest.comments && guest.comments.length > 0 ? `(${guest.comments.length})` : ""}
                            </span>
                          </Button>
                        </td>
                        {user?.isSuperAdmin && (
                        <td className="align-middle">
                          <Button
                            className="admin-delete-btn"
                            size="sm"
                            onClick={() => handleDeleteGuest(guest.id)}
                            title={t("delete")}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Guest Settings (per building) */}
      {buildingFilter && buildingFilter !== "all" && (
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body className="p-4">
            <h5 className="fw-semibold mb-3">
              {t("guestSettings")} — {getBuildingName(buildingFilter)}
            </h5>
            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>{t("monthlyDayLimit")}</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    value={settingsForm.monthlyDayLimit}
                    onChange={(e) =>
                      setSettingsForm({ ...settingsForm, monthlyDayLimit: Number(e.target.value) })
                    }
                  />
                  <Form.Text className="text-muted">{t("zeroMeansNoLimit")}</Form.Text>
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="showLimitWarning"
                    label={t("showLimitWarning")}
                    checked={settingsForm.showLimitWarning}
                    onChange={(e) =>
                      setSettingsForm({ ...settingsForm, showLimitWarning: e.target.checked })
                    }
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="showGuestHistory"
                    label={t("showGuestHistory")}
                    checked={settingsForm.showGuestHistoryToResidents}
                    onChange={(e) =>
                      setSettingsForm({ ...settingsForm, showGuestHistoryToResidents: e.target.checked })
                    }
                  />
                </Form.Group>
              </div>
            </div>
            <Button className="modal-primary-btn" onClick={handleSaveSettings}>
              {t("saveSettings")}
            </Button>
          </Card.Body>
        </Card>
      )}
    </Container>
  )
}
